import { Feature, FeatureMapping } from '../features';

type TFn = (key: string) => string;

export interface MenuOptions {
  features: FeatureMapping<boolean>;
  t: TFn;
  legacy?: boolean;
}

export interface MenuItem {
  title?: string;
  disabled?: boolean;
  href?: string;
  icon?: string;
  label: string;
  items?: MenuItem[];
}

export function getMenu({ features, t, legacy = false }: MenuOptions): MenuItem[] {
  if (legacy) {
    return buildLegacyMenus(features, t);
  }
  return buildShellMenus(features, t);
}

function buildShellMenus(features: FeatureMapping<boolean>, t: TFn): MenuItem[] {
  const menus: MenuItem[] = [
    {
      href: 'programs',
      icon: 'fa-grid-2',
      label: t('menu.programs.label'),
    },
    getPublishSection(features, t),
    getIntegrationSection(features, t),
    getTestingSection(features, t),
  ];

  menus.forEach(menu => {
    if (menu.items) {
      menu.items = menu.items.filter(subMenu => !subMenu.disabled);
      menu.disabled = !menu.items?.length;
    }
  });

  return menus.filter(menu => !menu.disabled);
}

function getPublishSection(features: FeatureMapping<boolean>, t: TFn): MenuItem {
  return {
    icon: 'fa-share-nodes',
    label: t('menu.publishing.label'),
    items: [
      {
        href: 'domains',
        label: t('topbar.tabs.domains-management'),
        disabled: !features[Feature.DOMAINS_MANAGEMENT].read,
      },
    ],
  };
}

function getIntegrationSection(features: FeatureMapping<boolean>, t: TFn): MenuItem {
  const outgoingWebhooksEnabled = features[Feature.OUTGOING_WEBHOOK].enabled;
  const loginPagesRead = features[Feature.LOGIN_PAGE];
  const interactionsRead = features[Feature.INTERACTION].read;
  return {
    icon: 'fa-plug',
    label: t('menu.integrations.label'),
    items: [
      {
        href: 'push-rules',
        label: t('topbar.tabs.push-rules'),
        disabled: !outgoingWebhooksEnabled,
      },
      {
        href: 'keys',
        label: t('topbar.tabs.custom-keys'),
        disabled: !features[Feature.CUSTOM_KEY].write,
      },
      {
        href: 'interactions',
        label: t('topbar.tabs.interactions'),
        disabled: !interactionsRead,
      },
      {
        href: 'integrations',
        label: t('topbar.tabs.connectors'),
        disabled: !outgoingWebhooksEnabled,
      },
      {
        href: 'login-pages',
        label: t('topbar.tabs.login-pages'),
        disabled: !loginPagesRead,
      },
    ],
  };
}

function getTestingSection(features: FeatureMapping<boolean>, t: TFn): MenuItem {
  return {
    icon: 'fa-vial',
    label: t('menu.testing.label'),
    items: [
      {
        href: 'event-reports',
        label: t('topbar.tabs.event-reports'),
        disabled: !features[Feature.EVENT_REPORT].enabled,
      },
      {
        href: 'test-users',
        label: t('topbar.tabs.test-users'),
        disabled: !features[Feature.TEST_USERS].read,
      },
    ],
  };
}

function buildLegacyMenus(features: FeatureMapping<boolean>, t: TFn): MenuItem[] {
  const { write: customKeyWrite } = features[Feature.CUSTOM_KEY];
  const { enabled: eventReportEnabled } = features[Feature.EVENT_REPORT];
  const { enabled: outgoingWebhooksEnabled } = features[Feature.OUTGOING_WEBHOOK];
  const { read: testUsersRead } = features[Feature.TEST_USERS];
  const { read: domainManagementRead } = features[Feature.DOMAINS_MANAGEMENT];
  const { read: loginPagesRead } = features[Feature.LOGIN_PAGE];
  const { read: interactionsRead } = features[Feature.INTERACTION];

  return [
    {
      title: customKeyWrite ? '' : t('shared.action.permission.required'),
      disabled: !customKeyWrite,
      href: 'customKeys',
      icon: 'fa-brackets-curly',
      label: t('topbar.tabs.custom-keys'),
    },
    {
      title: interactionsRead ? '' : t('shared.action.permission.required'),
      disabled: !interactionsRead,
      href: 'interactions',
      icon: 'fa-receipt',
      label: t('topbar.tabs.interactions'),
    },
    {
      title: eventReportEnabled ? '' : t('shared.action.permission.required'),
      disabled: !eventReportEnabled,
      href: 'eventReports',
      icon: 'fa-heart-pulse',
      label: t('topbar.tabs.event-reports'),
    },
    {
      title: domainManagementRead ? '' : t('shared.action.permission.required'),
      disabled: !domainManagementRead,
      href: 'domainsManagement',
      icon: 'fa-router',
      label: t('topbar.tabs.domains-management'),
    },
    {
      title: outgoingWebhooksEnabled ? '' : t('shared.action.permission.required'),
      disabled: !outgoingWebhooksEnabled,
      href: 'integrations',
      icon: 'fa-sitemap',
      label: t('topbar.tabs.integrations'),
    },
    {
      title: outgoingWebhooksEnabled ? '' : t('shared.action.permission.required'),
      disabled: !outgoingWebhooksEnabled,
      href: 'pushRules',
      icon: 'fa-upload',
      label: t('topbar.tabs.push-rules'),
    },
    {
      title: testUsersRead ? '' : t('shared.action.permission.required'),
      disabled: !testUsersRead,
      href: 'testUsers',
      icon: 'fa-user-secret',
      label: t('topbar.tabs.test-users'),
    },
    {
      title: loginPagesRead ? '' : t('shared.action.permission.required'),
      disabled: !loginPagesRead,
      href: 'loginPages',
      icon: 'fa-right-to-bracket',
      label: t('topbar.tabs.login-pages'),
    },
  ];
}
